/**
 * Premium Features JavaScript
 * Core functionality for all premium sections
 * Compatible with Dawn theme
 */

(function() {
  'use strict';

  // ==========================================================================
  // Utility Functions
  // ==========================================================================

  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const throttle = (func, limit) => {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  };

  // ==========================================================================
  // Intersection Observer for Scroll Animations
  // ==========================================================================

  const initScrollAnimations = () => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    document.querySelectorAll('.animate-on-scroll').forEach(el => {
      observer.observe(el);
    });
  };

  // ==========================================================================
  // Smooth Scroll
  // ==========================================================================

  const initSmoothScroll = () => {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        if (href === '#' || href === '#!') return;

        const target = document.querySelector(href);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  };

  // ==========================================================================
  // Lazy Load Images
  // ==========================================================================

  const initLazyLoad = () => {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            if (img.dataset.srcset) {
              img.srcset = img.dataset.srcset;
              img.removeAttribute('data-srcset');
            }
            img.classList.add('loaded');
            imageObserver.unobserve(img);
          }
        });
      });

      document.querySelectorAll('img[data-src], img[data-srcset]').forEach(img => {
        imageObserver.observe(img);
      });
    }
  };

  // ==========================================================================
  // Cart Functions
  // ==========================================================================

  const addToCart = async (variantId, quantity = 1) => {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const data = await response.json();
      
      // Trigger custom event for cart update
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      
      return data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  };

  const updateCartCount = async () => {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update cart count in header
      const cartCounts = document.querySelectorAll('.cart-count, [data-cart-count]');
      cartCounts.forEach(el => {
        el.textContent = cart.item_count;
      });

      return cart;
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  };

  // ==========================================================================
  // Form Validation
  // ==========================================================================

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateForm = (form) => {
    let isValid = true;
    const inputs = form.querySelectorAll('[required]');

    inputs.forEach(input => {
      const errorEl = input.nextElementSibling;
      
      if (!input.value.trim()) {
        isValid = false;
        input.classList.add('error');
        if (errorEl && errorEl.classList.contains('error-message')) {
          errorEl.textContent = 'This field is required';
        }
      } else if (input.type === 'email' && !validateEmail(input.value)) {
        isValid = false;
        input.classList.add('error');
        if (errorEl && errorEl.classList.contains('error-message')) {
          errorEl.textContent = 'Please enter a valid email';
        }
      } else {
        input.classList.remove('error');
        if (errorEl && errorEl.classList.contains('error-message')) {
          errorEl.textContent = '';
        }
      }
    });

    return isValid;
  };

  // ==========================================================================
  // Local Storage Utilities
  // ==========================================================================

  const storage = {
    set: (key, value) => {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        console.warn('localStorage not available');
      }
    },
    get: (key) => {
      try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : null;
      } catch (e) {
        console.warn('localStorage not available');
        return null;
      }
    },
    remove: (key) => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.warn('localStorage not available');
      }
    }
  };

  // ==========================================================================
  // Price Formatting
  // ==========================================================================

  const formatMoney = (cents) => {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    // Fallback formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  };

  // ==========================================================================
  // Modal/Popup Functions
  // ==========================================================================

  const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const trapFocus = (modal) => {
    const focusable = Array.from(modal.querySelectorAll(FOCUSABLE));
    if (!focusable.length) return;

    focusable[0].focus();

    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    modal._focusTrapHandler = handler;
    modal.addEventListener('keydown', handler);
  };

  const releaseFocus = (modal) => {
    if (modal._focusTrapHandler) {
      modal.removeEventListener('keydown', modal._focusTrapHandler);
      delete modal._focusTrapHandler;
    }
  };

  const openModal = (modalId) => {
    const modal = document.getElementById(modalId);
    if (modal) {
      modal.classList.add('active');
      document.body.style.overflow = 'hidden';
      trapFocus(modal);
    }
  };

  const closeModal = (modalId) => {
    const modal = document.getElementById(modalId);
    if (modal) {
      modal.classList.remove('active');
      document.body.style.overflow = '';
      releaseFocus(modal);
    }
  };

  // Close modals on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      document.querySelectorAll('.modal.active').forEach(modal => {
        modal.classList.remove('active');
        releaseFocus(modal);
      });
      document.body.style.overflow = '';
    }
  });

  // ==========================================================================
  // Performance Monitoring
  // ==========================================================================

  const measurePerformance = () => {
    if ('performance' in window && 'PerformanceObserver' in window) {
      // Measure Largest Contentful Paint (LCP)
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          console.log('LCP:', lastEntry.renderTime || lastEntry.loadTime);
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        console.log('LCP monitoring not supported');
      }

      // Measure First Input Delay (FID)
      try {
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach(entry => {
            console.log('FID:', entry.processingStart - entry.startTime);
          });
        });
        fidObserver.observe({ entryTypes: ['first-input'] });
      } catch (e) {
        console.log('FID monitoring not supported');
      }
    }
  };

  // ==========================================================================
  // Initialize Everything
  // ==========================================================================

  const init = () => {
    initScrollAnimations();
    initSmoothScroll();
    initLazyLoad();

    // Development only
    if (window.location.hostname === 'localhost' || 
        window.location.hostname.includes('.myshopify.com')) {
      measurePerformance();
    }

    console.log('Premium Features initialized');
  };

  // ==========================================================================
  // Export Public API
  // ==========================================================================

  window.PremiumFeatures = {
    addToCart,
    updateCartCount,
    validateForm,
    validateEmail,
    storage,
    formatMoney,
    openModal,
    closeModal,
    debounce,
    throttle
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
